import React, { useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";

import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { repairTranslatedLinks } from "../../utilities/googleTranslateA11y";
import "./Support.css";

/** Monday.com Service page — tickets queue workspace (same across environments). */
const MONDAY_TICKETS_WORKSPACE_HREF =
  "https://sriservices.monday.com/workspaces/13561310";

const VALID_CHANNELS = new Set([
  "Tax Sale",
  "Sheriff Sale",
  "Lien Assist",
  "Zeus",
  "SRI Website",
  "Other",
]);

function Support() {
  useDocumentTitle("Support");
  const [searchParams] = useSearchParams();

  const channel = useMemo(() => {
    const requested = searchParams.get("channel");
    return requested && VALID_CHANNELS.has(requested) ? requested : "SRI Website";
  }, [searchParams]);

  const apiEndpoint = useMemo(() => {
    const base = process.env.REACT_APP_API_BASE_URL || "";
    return `${base.replace(/\/$/, "")}/api/monday-ticket`;
  }, []);

  useEffect(() => {
    const scriptId = "sri-support-form-script";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = `${process.env.PUBLIC_URL || ""}/sri-support-form.js`;
      script.async = true;
      document.body.appendChild(script);
    }

    const repairSupportLinks = () => {
      repairTranslatedLinks(document.getElementById("support-page-content"));
      repairTranslatedLinks(document.getElementById("support-form-container"));
    };

    let supportObserver;
    const supportRoot = document.getElementById("support-page-content");
    const formRoot = document.getElementById("support-form-container");

    let repairTimer;
    const scheduleRepair = () => {
      clearTimeout(repairTimer);
      repairTimer = setTimeout(repairSupportLinks, 200);
    };

    if (supportRoot || formRoot) {
      supportObserver = new MutationObserver(() => {
        scheduleRepair();
      });
      if (supportRoot) {
        supportObserver.observe(supportRoot, { childList: true, subtree: true });
      }
      if (formRoot) {
        supportObserver.observe(formRoot, { childList: true, subtree: true });
      }
    }

    scheduleRepair();

    return () => {
      if (supportObserver) supportObserver.disconnect();
      clearTimeout(repairTimer);
    };
  }, []);

  return (
    <div id="support-page-content">
      <div className="supportHeader">
        <h1 className="supportTitle">Support</h1>
      </div>
      <div className="supportContent">
        <h2 className="supportSubTitle">Need to contact SRI?</h2>
        <p className="supportDescription">Please fill out the below form with your request.</p>
        <p>
          Use our{" "}
          <a
            className="supportLink notranslate"
            href={MONDAY_TICKETS_WORKSPACE_HREF}
            data-sri-href={MONDAY_TICKETS_WORKSPACE_HREF}
            target="_blank"
            rel="noopener noreferrer"
            translate="no"
            lang="en"
          >
            tickets workspace
          </a>{" "}
          on Monday.com to check on the status of an existing ticket.
        </p>
        <p>
          You may also try searching our{" "}
          <Link to="/knowledge-base" className="supportLink notranslate" translate="no" lang="en">
            knowledge base
          </Link>{" "}
          for helpful articles which may answer your question.
        </p>
      </div>
      <div id="support-form-container">
        {React.createElement("sri-support-form", {
          channel,
          "api-endpoint": apiEndpoint,
        })}
      </div>
    </div>
  );
}

export default Support;
